/**
 * SignOffReadinessCard — pre-flight check for case sign-off.
 *
 * Receives the sign_off_ready + sign_off_blockers slice from the
 * /api/v1/dashboard/accountant-payload/{claim_id} endpoint. Shows a
 * hueless "Ready" banner when all gates pass, or a prioritised list of
 * blockers when they don't — so the accountant knows what to fix
 * before signing off rather than discovering blockers after.
 *
 * Pure presentation for the readiness slice; when a `caseId` is provided it
 * ALSO renders the Sign-Off CTA at the base (two-step arm/confirm →
 * POST /api/v1/case-signoff/{caseId}). Without `caseId` the card is
 * unchanged (the legacy AccountantDashboard usage passes no caseId).
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '../../utils/api';

interface SignOffReadinessCardProps {
  /** True when all sign-off gates pass. */
  signOffReady?: boolean | null;
  /** List of human-readable blocker strings. Empty when ready. */
  signOffBlockers?: string[] | null;
  isLoading?: boolean;
  /** When provided, the Sign-Off CTA renders at the base and posts for this case. */
  caseId?: string;
  /** Called after a successful sign-off so the parent can refresh stats. */
  onSignedOff?: () => void;
}

export default function SignOffReadinessCard({
  signOffReady,
  signOffBlockers,
  isLoading,
  caseId,
  onSignedOff,
}: SignOffReadinessCardProps) {
  const [armed, setArmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Code-fact #2 (2026-08-22 pixel pass, "FIX THE CARD. Confirmed."): the
  // failed-POST branch was collapsing to a generic "SIGN-OFF FAILED — RETRY"
  // and silently dropping `res.blocking_issues` — the backend told us what's
  // blocking (case_signoff.py:145-150,185-190) and the UI had no right to
  // hide it. `postFailureBlockers` holds that server-authoritative list;
  // when non-null it REPLACES the pre-flight `signOffBlockers` list in the
  // render below (server > pre-flight — the POST re-evaluated readiness
  // live). It stays displayed through re-arm (retry doesn't clear it — the
  // user hasn't fixed anything yet) and is only replaced by a NEW POST
  // resolution: success short-circuits to the "Signed Off" state, another
  // failure overwrites it with the fresh list, or a `caseId` change resets it.
  const [postFailureBlockers, setPostFailureBlockers] = useState<string[] | null>(null);
  const [doneAt, setDoneAt] = useState<string | null>(null);

  useEffect(() => {
    setArmed(false);
    setSubmitting(false);
    setError(null);
    setPostFailureBlockers(null);
    setDoneAt(null);
  }, [caseId]);

  // Signed-off confirmation state (hueless) — short-circuits the readiness display.
  if (caseId && doneAt) {
    return (
      <div className="rounded-[2px] shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        <div className="flex flex-col items-center justify-center py-6 gap-3">
          <CheckCircle2 className="w-8 h-8" style={{ color: 'var(--color-text-dim)' }} />
          <div className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Signed Off</div>
          <div className="font-mono text-xs" style={{ color: 'var(--color-text-dim)' }}>
            {new Date(doneAt).toLocaleString()}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-[2px] shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Sign-Off Readiness</h3>
        </div>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin mr-2" style={{ color: 'var(--color-text-dim)' }} />
          <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Checking gates…</span>
        </div>
      </div>
    );
  }

  // Not yet loaded — null state
  if (signOffReady === undefined || signOffReady === null) {
    return (
      <div className="rounded-[2px] shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Sign-Off Readiness</h3>
        </div>
        <p className="text-xs text-center py-2" style={{ color: 'var(--color-text-dim)' }}>Not available</p>
      </div>
    );
  }

  // Code-fact #2: once a POST has failed with a structured `blocking_issues`
  // array, that server-authoritative list REPLACES the pre-flight
  // `signOffBlockers` prop everywhere below — the live readiness check ran
  // more recently than whatever produced the pre-flight payload.
  const blockers = postFailureBlockers ?? signOffBlockers ?? [];
  // The card was pre-flight-ready (armed CTA rendered) but the POST came
  // back blocked. Render the SAME "blockers" layout as the not-ready branch
  // (server list swapped in), just with a "SIGN-OFF FAILED" header instead
  // of "Sign-Off Readiness" — so the accountant sees exactly what the
  // backend rejected, not a generic "retry" with the reason hidden.
  const showFailedState = signOffReady === true && postFailureBlockers !== null;

  const ctaBlock = (() => {
    if (!caseId) return null;

    if (!signOffReady) {
      const reasonText = blockers.length === 0 ? 'NOT READY' : `${blockers.length} BLOCKER${blockers.length !== 1 ? 'S' : ''}`;
      return (
        <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 12, paddingTop: 12 }}>
          <button
            type="button"
            disabled
            className="w-full h-10 rounded-[2px] flex items-center justify-between px-3 cursor-not-allowed"
            style={{ backgroundColor: 'transparent', border: '1px solid var(--color-border)' }}
            title="Resolve all blockers to sign off."
          >
            <span className="text-[13px] font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
              Sign Off Case
            </span>
            <span className="font-mono text-[11px] uppercase" style={{ color: 'var(--color-text-dim)' }}>
              {reasonText}
            </span>
          </button>
        </div>
      );
    }

    return (
      <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 12, paddingTop: 12 }}>
        <button
          type="button"
          disabled={submitting}
          onClick={() => {
            if (submitting) return;
            if (!armed) {
              setArmed(true);
            } else {
              setSubmitting(true);
              setError(null);
              api.post(`/api/v1/case-signoff/${caseId}`, {}, { showErrorToast: false })
                .then((res: any) => {
                  if (res?.success && (res.action === 'signed_off' || res.action === 'already_signed_off')) {
                    setDoneAt(res.signed_off_at || new Date().toISOString());
                    setPostFailureBlockers(null);
                    onSignedOff?.();
                  } else {
                    // Code-fact #2: capture the server's blocking_issues (case_signoff.py
                    // SignOffResponse.blocking_issues, populated on action='blocked') so the
                    // failure view above can render the REAL reason instead of a generic
                    // retry message. `?? []` only for a malformed/absent field — an empty
                    // array here still renders the (rare) "reason unavailable" fallback row.
                    setPostFailureBlockers(Array.isArray(res?.blocking_issues) ? res.blocking_issues : []);
                    setError(res?.message || 'Sign-off blocked');
                    setArmed(false);
                  }
                })
                .catch(() => {
                  // Network/exception failure — no structured blocking_issues to show;
                  // postFailureBlockers stays null so this falls back to the generic
                  // inline error message below (existing behavior, unchanged).
                  setError('Sign-off failed');
                  setArmed(false);
                })
                .finally(() => setSubmitting(false));
            }
          }}
          onBlur={() => {
            if (!submitting) setArmed(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setArmed(false);
          }}
          className="w-full h-10 rounded-[2px] text-[13px] font-semibold uppercase tracking-wide focus-visible:ring-1 focus-visible:ring-brand focus-visible:ring-inset focus:outline-none"
          style={{
            backgroundColor: 'var(--color-brand)',
            color: 'var(--color-on-brand)',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!submitting) {
              e.currentTarget.style.backgroundColor = 'var(--color-brand-hover)';
            }
          }}
          onMouseLeave={(e) => {
            if (!submitting) {
              e.currentTarget.style.backgroundColor = 'var(--color-brand)';
            }
          }}
        >
          {submitting ? 'SIGNING OFF…' : armed ? 'CONFIRM SIGN OFF' : 'SIGN OFF CASE'}
        </button>
        {/* Structured failures (blocking_issues present) are rendered as the
            header + list above (showFailedState) — this inline line is only
            the fallback for a network/exception failure with no structured
            reason to show (postFailureBlockers stays null in that case). */}
        {error && postFailureBlockers === null && (
          <div className="font-mono text-[12px] mt-1.5" style={{ color: 'var(--color-blocker)' }} title={error}>
            SIGN-OFF FAILED — RETRY
          </div>
        )}
        {error && postFailureBlockers !== null && (
          <div className="font-mono text-[11px] mt-1.5" style={{ color: 'var(--color-text-dim)' }}>
            Resolve the blocker{blockers.length !== 1 ? 's' : ''} above, then retry.
          </div>
        )}
      </div>
    );
  })();

  if (signOffReady && !showFailedState) {
    return (
      <div className="rounded-[2px] shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-[2px]" style={{ backgroundColor: 'transparent' }}>
            <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Sign-Off Readiness</h3>
        </div>
        <div className="flex items-center gap-2 p-3 rounded-[2px]" style={{ backgroundColor: 'transparent', border: '1px solid var(--color-verified)' }}>
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-verified)' }} />
          <span className="text-sm font-medium" style={{ color: 'var(--color-verified)' }}>Ready to sign off</span>
        </div>
        <p className="text-xs mt-2" style={{ color: 'var(--color-text-dim)' }}>All gates passed</p>
        {ctaBlock}
      </div>
    );
  }

  // Not ready (pre-flight) OR a failed POST (Code-fact #2, showFailedState) —
  // both render this same blockers layout; `blockers` above already swaps in
  // the server's post-failure list when showFailedState is true, and the
  // header text below distinguishes the two cases for the accountant.
  return (
    <div className="rounded-[2px] shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-[2px]" style={{ backgroundColor: 'var(--color-blocker-tint)' }}>
            <XCircle className="w-4 h-4" style={{ color: 'var(--color-blocker)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {showFailedState ? 'SIGN-OFF FAILED' : 'Sign-Off Readiness'}
          </h3>
        </div>
        <span className="text-xs font-medium px-2 py-0.5 rounded-[2px]" style={{ color: 'var(--color-blocker)', backgroundColor: 'var(--color-blocker-tint)', border: '1px solid var(--color-blocker-tint-bd)' }}>
          {blockers.length} blocker{blockers.length !== 1 ? 's' : ''}
        </span>
      </div>

      {blockers.length === 0 ? (
        <div className="flex items-center gap-2 p-3 rounded-[2px]" style={{ backgroundColor: 'var(--color-blocker-tint)', border: '1px solid var(--color-blocker-tint-bd)' }}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-blocker)' }} />
          <span className="text-xs" style={{ color: 'var(--color-blocker)' }}>Not ready — reason unavailable</span>
        </div>
      ) : (
        <ul className="space-y-2">
          {blockers.map((blocker, i) => (
            <li
              key={i}
              className="flex items-start gap-2 p-2.5 rounded-[2px]"
              style={{ backgroundColor: 'var(--color-blocker-tint)', border: '1px solid var(--color-blocker-tint-bd)' }}
            >
              <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-blocker)' }} />
              <span className="text-xs leading-snug" style={{ color: 'var(--color-blocker)' }}>{blocker}</span>
            </li>
          ))}
        </ul>
      )}
      {ctaBlock}
    </div>
  );
}
